import React, { useContext, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/store";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthProvider";

const Sidebar = ({ isSidebarOpen, toggleSidebar }) => {
  const { t } = useTranslation();
  const { mainStore } = useStore();
  const { auth } = useContext(AuthContext);

  const HomeMenuItems = [
    {
      path: "/Home",
      displayName: t("home"),
      iconClass: "ti ti-layout-dashboard",
      roles: ["admin", "user"],
      subCategory: ["Home"],
      menuNo: mainStore.HomeMenuNo,
    },
    {
      path: "/ToolsCatalog",
      displayName: t("toolsCatalog"),
      iconClass: "ti ti-tools",
      roles: ["admin", "user"],
      subCategory: ["Home"],
      menuNo: mainStore.ToolsCatalogMenuNo,
    },
  ];


  // Administration menu - separated into Approvals and User Management
  const SettingsMenuItems = [
    // Approvals section (most frequent admin task)
    {
      path: "/ContentApprovals/List",
      displayName: t("contentApprovals"),
      iconClass: "ti ti-clipboard-check",
      roles: ["admin"],
      subCategory: ["approvals"],
      menuNo: mainStore.ContentApprovalMenuNo,
    },
    {
      path: "/UserApprovals/List",
      displayName: t("userApprovals"),
      iconClass: "ti ti-user-check",
      roles: ["admin"],
      subCategory: ["approvals"],
      menuNo: mainStore.UserApprovalMenuNo,
    },
    // User Management section
    {
      path: "/User/List",
      displayName: t("users"),
      iconClass: "ti ti-users",
      roles: ["admin"],
      subCategory: ["userManagement"],
      menuNo: mainStore.UserMenuNo,
    },
    {
      path: "/Role/List",
      displayName: t("roles"),
      iconClass: "ti ti-shield",
      roles: ["admin"],
      subCategory: ["userManagement"],
      menuNo: mainStore.RoleMenuNo,
    },
    // Content Management section (About & Research Publications)
    {
      path: "/Admin/ResearchPublications",
      displayName: t("researchPublications"),
      iconClass: "ti ti-book",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.ResearchPublicationsAdminMenuNo,
    },
    {
      path: "/Admin/ResearchTeam",
      displayName: t("researchTeam"),
      iconClass: "ti ti-users-group",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.ResearchTeamAdminMenuNo,
    },
    {
      path: "/Admin/PlatformHistory",
      displayName: t("platformHistory"),
      iconClass: "ti ti-history",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.PlatformHistoryAdminMenuNo,
    },
    {
      path: "/Admin/SystemVersions",
      displayName: t("systemVersions"),
      iconClass: "ti ti-versions",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.SystemVersionsAdminMenuNo,
    },
    {
      path: "/Admin/KeyFeatures",
      displayName: t("keyFeatures"),
      iconClass: "ti ti-star",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.KeyFeaturesAdminMenuNo,
    },
    {
      path: "/Admin/MCDMMethods",
      displayName: t("mcdmMethods"),
      iconClass: "ti ti-chart-dots",
      roles: ["admin"],
      subCategory: ["contentManagement"],
      menuNo: mainStore.MCDMMethodsAdminMenuNo,
    },
  ];

  // Data Management menu - organized by data dependency order
  // Order: 1.TypeOfTool → 2.Panel → 3.Categories → 4.Subcategories → 5.CriteriaManager → 6.ToolPassport → 7.ToolFeature
  const ToolPassportsMenuItems = [
    // Foundation Data (must exist first)
    {
      path: "/TypeOfTool/List",
      displayName: t("typeOfTools"),
      iconClass: "ti ti-category",
      roles: ["admin"],
      subCategory: ["foundationData"],
      menuNo: mainStore.TypeOfToolMenuNo,
    },
    // Classification Hierarchy (Panel → Category → Subcategory)
    {
      path: "/Panel/List",
      displayName: t("panels"),
      iconClass: "ti ti-layout-grid",
      roles: ["admin"],
      subCategory: ["classificationHierarchy"],
      menuNo: mainStore.PanelMenuNo,
    },
    {
      path: "/Category/List",
      displayName: t("categories"),
      iconClass: "ti ti-folder",
      roles: ["admin"],
      subCategory: ["classificationHierarchy"],
      menuNo: mainStore.CategoryMenuNo,
    },
    {
      path: "/Subcategory/List",
      displayName: t("subcategories"),
      iconClass: "ti ti-folders",
      roles: ["admin"],
      subCategory: ["classificationHierarchy"],
      menuNo: mainStore.SubcategoryMenuNo,
    },
    // Tool Data (depends on classification hierarchy)
    {
      path: "/ToolPassport/List",
      displayName: t("toolPassports"),
      iconClass: "ti ti-tool",
      roles: ["admin"],
      subCategory: ["toolData"],
      menuNo: mainStore.ToolPassportMenuNo,
    },
  ];


  // Tool Selection menu - Configuration and Selection
  // Order: 5.CriteriaManager → 7.ToolFeature → then ToolSelection works
  const ToolSelectionMenuItems = [
    // Criteria Configuration (step 5 in data dependency)
    {
      path: "/CriteriaManager",
      displayName: t("criteriaManager"),
      iconClass: "ti ti-adjustments",
      roles: ["admin"],
      subCategory: ["criteriaSetup"],
      menuNo: mainStore.CriteriaManagerMenuNo,
    },
    // Tool Features (step 7 - links criteria to tools)
    {
      path: "/ToolFeature/List",
      displayName: t("toolFeatures"),
      iconClass: "ti ti-list-details",
      roles: ["admin"],
      subCategory: ["featureConfig"],
      menuNo: mainStore.ToolFeatureMenuNo,
    },
    // Use Tool Selection (available after all data is set up)
    {
      path: "/ToolSelection/Selection",
      displayName: t("selectTool"),
      iconClass: "ti ti-target",
      roles: ["admin", "user"],
      subCategory: ["useSelection"],
      menuNo: mainStore.ToolSelectionMenuNo,
    },
  ];

  const ResearchMenuItems = [
    {
      path: "/Research/MethodComparison",
      displayName: t("methodComparison"),
      iconClass: "ti ti-chart-bar",
      roles: ["admin"],
      subCategory: ["researchTools"],
      menuNo: mainStore.MethodComparisonMenuNo,
    },
  ];

  // User submission menu items (for 'user' role)
  // Organized in logical workflow: Submit → View My Content → Track Requests
  const UserSubmissionMenuItems = [
    // Section 1: Submit New Content
    {
      path: "/SubmitToolPassport",
      displayName: t("submitNewTool"),
      iconClass: "ti ti-plus",
      roles: ["user", "admin"],
      subCategory: ["submitContent"],
      menuNo: mainStore.SubmitToolPassportMenuNo,
    },
    {
      path: "/SubmitCriteria",
      displayName: t("submitNewCriteria"),
      iconClass: "ti ti-plus",
      roles: ["user", "admin"],
      subCategory: ["submitContent"],
      menuNo: mainStore.SubmitCriteriaMenuNo,
    },
    {
      path: "/SubmitToolFeature",
      displayName: t("submitToolFeature"),
      iconClass: "ti ti-plus",
      roles: ["user", "admin"],
      subCategory: ["submitContent"],
      menuNo: mainStore.SubmitToolFeatureMenuNo,
    },
    // Section 2: View My Submitted Content
    {
      path: "/MySubmissions",
      displayName: t("mySubmissions"),
      iconClass: "ti ti-list",
      roles: ["user", "admin"],
      subCategory: ["myContent"],
      menuNo: mainStore.MySubmissionsMenuNo,
    },
    {
      path: "/MyTools",
      displayName: t("myTools"),
      iconClass: "ti ti-tool",
      roles: ["user", "admin"],
      subCategory: ["myContent"],
      menuNo: mainStore.MyToolsMenuNo,
    },
    {
      path: "/MyCriteria",
      displayName: t("myCriteria"),
      iconClass: "ti ti-checklist",
      roles: ["user", "admin"],
      subCategory: ["myContent"],
      menuNo: mainStore.MyCriteriaMenuNo,
    },
    {
      path: "/MyToolFeatures",
      displayName: t("myToolFeatures"),
      iconClass: "ti ti-list-details",
      roles: ["user", "admin"],
      subCategory: ["myContent"],
      menuNo: mainStore.MyToolFeaturesMenuNo,
    },
    // Section 3: Track Requests
    {
      path: "/MyRequests",
      displayName: t("myRequests"),
      iconClass: "ti ti-history",
      roles: ["user", "admin"],
      subCategory: ["trackRequests"],
      menuNo: mainStore.MyRequestsMenuNo,
    },
  ];

 
  // Check if user has access to a menu item based on role
  const hasRoleAccess = (item) => {
    if (!item.roles || item.roles.length === 0) return true;
    const userRole = auth?.role?.toLowerCase();
    return item.roles.some(role => role.toLowerCase() === userRole);
  };

  // Check if user has access to a menu item based on components permission
  const hasComponentAccess = (item) => {
    // Admin role has access to all menu items
    const userRole = auth?.role?.toLowerCase();
    if (userRole === 'admin') {
      return true;
    }
    // If no components defined in auth, or empty array, fall back to role-based access only
    if (!auth?.components || !Array.isArray(auth.components) || auth.components.length === 0) {
      return hasRoleAccess(item);
    }
    // Home is always accessible to authenticated users
    if (item.path === "/Home") return true;
    // User submission and contribution paths are always allowed for users with those roles
    const userPaths = [
      "/MySubmissions", "/MyTools", "/MyCriteria", "/MyToolFeatures", "/MyRequests",
      "/SubmitToolPassport", "/SubmitCriteria", "/SubmitToolFeature"
    ];
    if (userPaths.includes(item.path)) return true;
    // Check if path is in allowed components
    return auth.components.includes(item.path);
  };

  const renderMenuItems = (menuItems, menuCategory) => {
    const menuItemsResult = menuItems
      .filter(item =>
        item.subCategory.includes(menuCategory) &&
        hasRoleAccess(item) &&
        hasComponentAccess(item)
      )
      .map(item => (
        <li className="nav-item" key={item.menuNo}>
          <Link
            to={item.path}
            className={`nav-link ${mainStore.menuNo === item.menuNo ? "active" : ""}`}
            onClick={toggleSidebar}
          >
            {item.displayName}
          </Link>
        </li>
      ));

    if (menuItemsResult.length === 0) return null;

    return (
      <>
        <li className="nav-item mt-3" key={menuCategory}>
          <h6 className="ms-2 text-uppercase text-xs text-danger font-weight-bolder ">
            {t(menuCategory)}
          </h6>
        </li>
        <hr className="horizontal  mt-0 mb-1" />
        {menuItemsResult}
      </>
    );
  };

  return (
    <div>
      <ul className="nav flex-column mt-2">
        {/* Home */}
        {mainStore.menuType === 200 && renderMenuItems(HomeMenuItems, "Home")}

        {/* Data Management - Following data dependency order:
            1. TypeOfTool (foundation) → 2-4. Panel/Category/Subcategory (hierarchy) → 6. ToolPassport */}
        {mainStore.menuType === 300 && renderMenuItems(ToolPassportsMenuItems, "foundationData")}
        {mainStore.menuType === 300 && renderMenuItems(ToolPassportsMenuItems, "classificationHierarchy")}
        {mainStore.menuType === 300 && renderMenuItems(ToolPassportsMenuItems, "toolData")}

        {/* Tool Selection - 5. CriteriaManager → 7. ToolFeature → Use ToolSelection */}
        {mainStore.menuType === 400 && renderMenuItems(ToolSelectionMenuItems, "criteriaSetup")}
        {mainStore.menuType === 400 && renderMenuItems(ToolSelectionMenuItems, "featureConfig")}
        {mainStore.menuType === 400 && renderMenuItems(ToolSelectionMenuItems, "useSelection")}

        {/* Research */}
        {mainStore.menuType === 500 && renderMenuItems(ResearchMenuItems, "researchTools")}

        {/* User Submissions - Submit, View, Track */}
        {mainStore.menuType === 600 && renderMenuItems(UserSubmissionMenuItems, "submitContent")}
        {mainStore.menuType === 600 && renderMenuItems(UserSubmissionMenuItems, "myContent")}
        {mainStore.menuType === 600 && renderMenuItems(UserSubmissionMenuItems, "trackRequests")}

        {/* Administration - Approvals, User Management, and Content Management */}
        {mainStore.menuType === 100 && renderMenuItems(SettingsMenuItems, "approvals")}
        {mainStore.menuType === 100 && renderMenuItems(SettingsMenuItems, "userManagement")}
        {mainStore.menuType === 100 && renderMenuItems(SettingsMenuItems, "contentManagement")}

      </ul>
    </div>
  );
}

export default observer(Sidebar);

